import { useCallback } from 'react';
import { FORM_NAMES } from '~/app/constants';
import { SPIN_GENERIC } from './useSpinner';

/**
 * useTransferData
 *
 * Owns the year-to-year / transfer-data methods that were previously class
 * methods on FormViewer:
 *
 *   openTransferDataOptionsModal() — enable YTY options and open modal
 *   onYesTransferData(rtnID)       — accept YTY transfer (no options)
 *   onYesTransferDataOption(rtnID, option) — accept YTY with Reload/StateOnly
 *   onNoTransferData()             — reject YTY transfer
 *   onCancelTransferData()         — cancel / close without transfer
 *   onCancelCurrentYear()          — cancel the current-year option modal
 *   openCurrentYearModal()         — switch from YTY modal to CY modal
 *
 * Returns all methods for use in JSX and in the socketCallbacksRef bridge.
 */
const useTransferData = ({
  calcAPIRef,
  formNavRef, // kept-current ref to useFormNavigation result
  miscState, // useMiscState result
  spinner, // useSpinner result
  // Redux action dispatchers
  openYTYDialog,
  closeYTYDialog,
  openCurrentYearModal,
  closeCurrentYearModal,
}) => {
  const openTransferDataOptionsModal = useCallback(() => {
    miscState.setUseYtyOptions(true);
    openYTYDialog();
  }, [miscState, openYTYDialog]);

  const onYesTransferData = useCallback(
    rtnID => {
      closeYTYDialog(); // prevent multiple events
      spinner.startSpin(SPIN_GENERIC); // Stopped by update_form, then a form_load to CDS occurs
      calcAPIRef.current.ReturnYearToYear(formNavRef.current.activeForm, rtnID);
    },
    [calcAPIRef, closeYTYDialog, formNavRef, spinner],
  );

  const onYesTransferDataOption = useCallback(
    (rtnID, option) => {
      closeYTYDialog(); // prevent multiple events
      spinner.startSpin(SPIN_GENERIC); // Stopped by update_form, then a form_load to CDS occurs
      calcAPIRef.current.ReturnYearToYearOption(formNavRef.current.activeForm, rtnID, option);
    },
    [calcAPIRef, closeYTYDialog, formNavRef, spinner],
  );

  const onNoTransferData = useCallback(() => {
    calcAPIRef.current.ReturnYearToYear(FORM_NAMES.CDS, 0); // No selected
    closeYTYDialog();
  }, [calcAPIRef, closeYTYDialog]);

  const onCancelTransferData = useCallback(() => {
    closeYTYDialog();
    if (!miscState.useYtyOptions) {
      calcAPIRef.current.ReturnFormLoad('000A00', '', '', '');
    }
    miscState.setUseYtyOptions(false);
  }, [calcAPIRef, closeYTYDialog, miscState]);

  const onCancelCurrentYear = useCallback(() => {
    closeCurrentYearModal();
  }, [closeCurrentYearModal]);

  const openCurrentYearModalHandler = useCallback(() => {
    closeYTYDialog();
    openCurrentYearModal();
  }, [closeYTYDialog, openCurrentYearModal]);

  return {
    openTransferDataOptionsModal,
    onYesTransferData,
    onYesTransferDataOption,
    onNoTransferData,
    onCancelTransferData,
    onCancelCurrentYear,
    openCurrentYearModal: openCurrentYearModalHandler,
  };
};

export default useTransferData;
